"use client";

// THE VIEW CHIPS ARRIVE WITH THEIR PAGE (law_stage, 2026-10-19, John: "when we're
// switching between spaces, the view chips can sometimes fly in and out of view
// very glitchily … they [should] appear in the order they appear and visually
// with the rest of the page").
//
// A step between sibling spaces keeps the navigator (its ticker glides), but the
// chips under it belong to the place, not the navigator: a folder's views, a
// panel's own (NavChips). They used to swap in place the instant the place
// changed, while the content handed over on its own clock, and the navigator's
// FLIP flew the new row in from wherever the old one, or an empty slot, had been.
//
// Now the chips hand over exactly as the content does (lib/stage/handoff): a
// still copy of the old row stands where it stood and drifts toward the side you
// left by, while the new row waits at its first frame and arrives a beat later,
// on the same timings and from the same side as the page below it. One row at a
// time, never two half-lit together.

import { Component, createRef, type ReactNode } from "react";
import { captureGhost, playGhost, sequenceArrival, type Ghost } from "../../lib/stage/handoff";

/** a touch past the arrival's longest run, after which the row stops being "arriving" */
const SETTLED_MS = 900;

export default class ChipsHandoff extends Component<{
  /** the place the chips belong to: a new value hands the row over */
  placeKey: string;
  /** +1 the place is to the right (the row arrives from the right), −1 to the left */
  dir: 1 | -1;
  children: ReactNode;
}> {
  box = createRef<HTMLDivElement>();
  ghost = createRef<HTMLDivElement>();
  settle = 0;

  getSnapshotBeforeUpdate(prev: { placeKey: string }): (Ghost & { at: number }) | null {
    if (prev.placeKey === this.props.placeKey) return null;
    const box = this.box.current;
    // nothing to hand over: the place before had no chips
    if (!box || !box.querySelector(".chip")) return null;
    const g = captureGhost(box, null, null);
    // where the row stood in the navigator: the new place's line about itself
    // may be taller or shorter, and the copy must leave from where it WAS
    return g ? { ...g, at: box.offsetTop + (box.parentElement?.offsetTop ?? 0) } : null;
  }

  componentDidUpdate(prev: { placeKey: string }, _s: unknown, snap: (Ghost & { at: number }) | null) {
    if (prev.placeKey === this.props.placeKey) return;
    const box = this.box.current;
    const layer = this.ghost.current;
    if (!box) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (layer) {
      const now = box.offsetTop + (box.parentElement?.offsetTop ?? 0);
      layer.style.top = snap ? `${snap.at - now}px` : "";
    }
    const leaving = snap && layer ? playGhost(snap, layer, this.props.dir) : Promise.resolve();
    sequenceArrival(box, leaving);
    // once it has arrived the row is simply there: a panel that redraws its row
    // later (a count arriving) never replays the entrance
    window.clearTimeout(this.settle);
    const seq = box.dataset.handoffSeq;
    this.settle = window.setTimeout(() => {
      if (box.dataset.handoffSeq === seq) box.removeAttribute("data-handoff");
    }, SETTLED_MS);
  }

  componentWillUnmount() {
    window.clearTimeout(this.settle);
  }

  render() {
    return (
      <div className="os-chips-box">
        <div
          ref={this.box}
          className="os-chips-in"
          style={{ ["--enter-x" as string]: this.props.dir >= 0 ? "14px" : "-14px" } as React.CSSProperties}
        >
          {this.props.children}
        </div>
        <div ref={this.ghost} className="os-chips-ghost stage-head-ghost" aria-hidden />
      </div>
    );
  }
}
